import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { searchAPI } from "../axios/search-axios";
import useFeedbackMonitor from "../custom-hooks/useFeedbackMonitor";
import { SEMANTIC_SEARCH_FEATURE_ENABLED } from "../config/featureFlags";
import { trackGoogleAnalyticsEvent } from "../googleAnalytics";

const ITEMS_PER_PAGE = 20;
const MAX_PAGES_FROM_API = 100;

const DEFAULT_API_SORT_PARAM = "date";
const DEFAULT_API_SORT_TYPE = "asc";
const SORT_PARAM_API_MAP = {
  title: "coll_name",
  date: "date",
  coll_location: "coll_location",
  coll_source: "coll_source",
  coll_name_arabic: "coll_name_arabic",
  coll_location_arabic: "coll_location_arabic",
  coll_source_arabic: "coll_source_arabic",
};


const useSearchResults = () => {
  const location = useLocation();
  const searchString = location.state ? location.state.searchString : "";
  const getStoredOriginalSearchTerm = () => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const stored = localStorage.getItem("originalSearchTerm");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const originalSearchString =
    (location.state && location.state.originalSearchString) ??
    getStoredOriginalSearchTerm() ??
    "";

  const [searchResults, setSearchResults] = useState(
    location.state?.searchResults || []
  );

  const getStoredRagSummary = () => {
    if (!SEMANTIC_SEARCH_FEATURE_ENABLED) {
      return "";
    }
    try {
      const stored = localStorage.getItem("ragSummary");
      if (!stored) return "";
      return JSON.parse(stored);
    } catch (error) {
      console.error("Failed to parse stored rag summary", error);
      return "";
    }
  };

  const initialRagSummary = SEMANTIC_SEARCH_FEATURE_ENABLED
    ? location.state && Object.prototype.hasOwnProperty.call(location.state, "rag")
      ? location.state.rag || ""
      : getStoredRagSummary()
    : "";

  const [ragSummary, setRagSummary] = useState(initialRagSummary);

  useEffect(() => {
    if (!SEMANTIC_SEARCH_FEATURE_ENABLED) {
      return;
    }
    try {
      localStorage.setItem("ragSummary", JSON.stringify(ragSummary || ""));
    } catch (error) {
      console.error("Failed to persist rag summary", error);
    }
    if (location.state) {
      location.state.rag = ragSummary;
    }
  }, [ragSummary, location]);

  // console.log("searchResults", searchResults);
  // console.log("searchString",searchString)

  const [searchResultSets, setSearchResultSets] = useState({});

  const originalResultsRef = useRef(location.state?.searchResults || []);

  const {
    showFeedbackPopup,
    handleCloseFeedbackPopup,
    fingerprint,
    isNewUser,
  } = useFeedbackMonitor();

  const readSemanticEnabled = () => {
    if (!SEMANTIC_SEARCH_FEATURE_ENABLED) {
      return false;
    }
    const storedValue = localStorage.getItem("semanticSearchEnabled");
    return storedValue ? JSON.parse(storedValue) : false;
  };

  //Filter Modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const sortResultsClientSide = (docs = [], columnName, sortType) => {
    const direction = sortType === "desc" ? -1 : 1;

    const getValue = (row) => {
      if (!row) return null;
      if (columnName === "title") {
        return row.title || "";
      }
      if (columnName === "number_of_pages") {
        const value = row.number_of_pages;
        if (typeof value === "number") return value;
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      return row[columnName] ?? "";
    };

    return [...docs].sort((a, b) => {
      const valueA = getValue(a);
      const valueB = getValue(b);

      if (typeof valueA === "number" && typeof valueB === "number") {
        return (valueA - valueB) * direction;
      }

      const stringA = valueA ? valueA.toString() : "";
      const stringB = valueB ? valueB.toString() : "";
      return stringA.localeCompare(stringB, undefined, { sensitivity: "base" }) * direction;
    });
  };

  const updateResults = (
    results = [],
    { persistOriginal = false, rag } = {}
  ) => {
    const normalizedResults = Array.isArray(results) ? [...results] : [];

    setSearchResultSets({
      [1]: normalizedResults,
    });
    setSearchResults(normalizedResults);
    if (location.state) {
      location.state.searchResults = normalizedResults;
    }
    localStorage.setItem("searchResults", JSON.stringify(normalizedResults));
    setCurrentPage(1);
    setLoading(false);

    if (persistOriginal) {
      originalResultsRef.current = [...normalizedResults];
    }

    if (SEMANTIC_SEARCH_FEATURE_ENABLED && rag !== undefined) {
      const normalizedRag = rag || "";
      setRagSummary(normalizedRag);
      if (location.state) {
        location.state.rag = normalizedRag;
      }
      try {
        localStorage.setItem("ragSummary", JSON.stringify(normalizedRag));
      } catch (error) {
        console.error("Failed to persist rag summary", error);
      }
    }
  };

  const applyLocalSort = (sortType, columnName) => {
    const baseResults = originalResultsRef.current || [];

    if (sortType === "neutral") {
      updateResults(baseResults, { persistOriginal: false });
      return;
    }

    const sorted = sortResultsClientSide(baseResults, columnName, sortType);
    updateResults(sorted, { persistOriginal: false });
  };

  const fetchAdditionalResults = async (page) => {
    try {
      setLoading(true);
      const filters = JSON.parse(localStorage.getItem("filters"));
      let requestBody = {
        search_after: searchAfter,
        search_term: localStorage.getItem("searchTerm")
          ? localStorage.getItem("searchTerm").replace(/^"(.*)"$/, "$1")
          : "",
        match_type: localStorage.getItem("matchType")
          ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
          : "any",
        sort_type: "desc",
      };

      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        requestBody.semantic_search_enabled = readSemanticEnabled();
      }
      if (filters)
        requestBody = {
          ...filters,
          ...requestBody,
        };
      const response = await searchAPI.put("", requestBody);
      const newSearchResults = response.data.docs;

      if (
        SEMANTIC_SEARCH_FEATURE_ENABLED &&
        Object.prototype.hasOwnProperty.call(response.data, "rag")
      ) {
        const ragValue = response.data.rag || "";
        setRagSummary(ragValue);
        if (location.state) {
          location.state.rag = ragValue;
        }
        try {
          localStorage.setItem("ragSummary", JSON.stringify(ragValue));
        } catch (error) {
          console.error("Failed to persist rag summary", error);
        }
      }

      totalPages =
        totalPages + Math.ceil(newSearchResults.length / ITEMS_PER_PAGE);
      setSearchAfter(response.data.search_after);
      setSearchResultSets((prevSearchResultSets) => ({
        ...prevSearchResultSets,
        [page]: newSearchResults,
      }));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching additional results:", error);
      setLoading(false);
    }
  };

  useEffect(() => {}, [searchResultSets]);

  //Whenever location.state.searchResults changes, update the searchResultSets state
  useEffect(() => {
    if (location.state?.searchResults) {
      const initialResults = location.state.searchResults;
      originalResultsRef.current = [...initialResults];
      setSearchResults(initialResults);
      setSearchResultSets({
        [1]: initialResults,
      });
      if (
        SEMANTIC_SEARCH_FEATURE_ENABLED &&
        Object.prototype.hasOwnProperty.call(location.state, "rag")
      ) {
        const ragValue = location.state.rag || "";
        setRagSummary(ragValue);
        try {
          localStorage.setItem("ragSummary", JSON.stringify(ragValue));
        } catch (error) {
          console.error("Failed to persist rag summary", error);
        }
      }
    }
  }, [location.state?.searchResults]);

  //Headings sorting - whenever a user clicks a heading on the table, send existing filters / parameters plus "sort_param" (date, etc depending on what was clicked) and "sort_type" (asc/desc - asc on first click, desc on next, and so on) to the same /search endpoint. Take the new results, set them in searchResultSets, set the pagination page number to 1 and update the table with the new results.

  // Below state keeps track of sortParam( basically sort by column)
  const [sortParam, setSortParam] = useState("date");
  const [nextSortType, setNextSortType] = useState("desc");

  const fetchSortedData = async (curSortType, columnName) => {
    const apiSortParam = SORT_PARAM_API_MAP[columnName];

    if (!apiSortParam) {
      applyLocalSort(curSortType, columnName);
      return;
    }

    try {
      setLoading(true);
      const filters = JSON.parse(localStorage.getItem("filters"));
      let requestBody = {
        search_term: localStorage.getItem("searchTerm")
          ? localStorage.getItem("searchTerm").replace(/^"(.*)"$/, "$1")
          : "",
        match_type: localStorage.getItem("matchType")
          ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
          : "any",
        sort_param:
          curSortType === "neutral" ? DEFAULT_API_SORT_PARAM : apiSortParam,
        sort_type: curSortType === "neutral" ? DEFAULT_API_SORT_TYPE : curSortType,
      };

      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        requestBody.semantic_search_enabled = readSemanticEnabled();
      }

      if (filters)
        requestBody = {
          ...filters,
          ...requestBody,
        };

      const response = await searchAPI.put("", requestBody);
      let newSearchResults = response.data.docs || [];

      if (columnName === "title" && curSortType !== "neutral") {
        newSearchResults = sortResultsClientSide(
          newSearchResults,
          columnName,
          curSortType
        );
      }

      totalPages =
        totalPages + Math.ceil(newSearchResults.length / ITEMS_PER_PAGE);

      updateResults(newSearchResults, {
        persistOriginal: true,
        rag: response.data.rag,
      });
    } catch (error) {
      console.error("Error fetching additional results:", error);
      setLoading(false);
    }
  };

  const handleHeadingClick = async (columnName) => {
    let sortTypeToApply = "asc";
    let newNextSortType = "desc";

    if (sortParam === columnName) {
      sortTypeToApply = nextSortType;
      if (nextSortType === "asc") newNextSortType = "desc";
      else if (nextSortType === "desc") newNextSortType = "neutral";
      else newNextSortType = "asc";
    } else {
      setSortParam(columnName);
    }

    if (columnName === "number_of_pages") {
      applyLocalSort(sortTypeToApply, columnName);
    } else {
      await fetchSortedData(sortTypeToApply, columnName);
    }

    setNextSortType(newNextSortType);
  };

  const sortArrowElement = (col_type) => {
    // asc -> desc -> neutral -> asc -> desc -> neutral -> ...
    if (col_type === sortParam) {
      if (nextSortType === "asc") {
        // current neutral
        return " ▽△";
      } else if (nextSortType === "desc") {
        // current asc
        return " ▲";
      } else {
        // next neutral, current desc
        return " ▼";
      }
    } else {
      return " △▽";
    }
  };

  const [searchAfter, setSearchAfter] = useState(
    location.state?.searchAfter || ""
  );

  // Loading
  const [loading, setLoading] = useState(false);

  //Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [analytics, setAnalytics] = useState(location.state?.analytics || {});
  const [filtersResetToken, setFiltersResetToken] = useState(
    location.state?.filtersResetToken ?? null
  );

  useEffect(() => {
    if (location.state?.analytics) {
      setAnalytics(location.state.analytics);
      localStorage.setItem(
        "analytics",
        JSON.stringify(location.state.analytics)
      );
    }
  }, [location.state?.analytics]);

  useEffect(() => {
    if (location.state && "filtersResetToken" in location.state) {
      setFiltersResetToken(location.state.filtersResetToken ?? null);
    }
  }, [location.state?.filtersResetToken]);

  const lastKey = Math.max(...Object.keys(searchResultSets).map(Number));

  let totalPages = 0;
  if (searchResultSets[lastKey])
    totalPages =
      lastKey -
      1 +
      Math.ceil(searchResultSets[lastKey].length / ITEMS_PER_PAGE);

  //For current page
  let startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  let endIndex = startIndex + ITEMS_PER_PAGE;
  let displayedRows = [];
  let currentSearchResults;
  let hashMapKey =
    Math.floor(currentPage / MAX_PAGES_FROM_API) * MAX_PAGES_FROM_API + 1;
  if (searchResultSets[hashMapKey] && currentPage >= hashMapKey) {
    currentSearchResults = searchResultSets[hashMapKey];
    localStorage.setItem("searchResults", JSON.stringify(currentSearchResults));
    startIndex = (currentPage - hashMapKey) * ITEMS_PER_PAGE;
    endIndex = startIndex + ITEMS_PER_PAGE;
    displayedRows = currentSearchResults?.slice(startIndex, endIndex);
  }
  //For the bottleneck pages where data loads
  else if (currentPage === hashMapKey - 1) {
    let prevHashmapKey = hashMapKey - MAX_PAGES_FROM_API;
    currentSearchResults = searchResultSets[prevHashmapKey];
    localStorage.setItem("searchResults", JSON.stringify(currentSearchResults));
    startIndex = (currentPage - prevHashmapKey) * ITEMS_PER_PAGE;
    endIndex = startIndex + ITEMS_PER_PAGE;
    displayedRows = currentSearchResults?.slice(startIndex, endIndex);
  }

  const emptyRowsCount = ITEMS_PER_PAGE - displayedRows.length;

  const handlePageChange = (page) => {
    setCurrentPage(page);

    const hashMapKey =
      Math.floor(page / MAX_PAGES_FROM_API) * MAX_PAGES_FROM_API + 1;
    if (!searchResultSets[hashMapKey]) {
      fetchAdditionalResults(hashMapKey);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSearchResultSets((prevSearchResultSets) => ({
      [1]: searchResults || location.state.searchResults,
    }));
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("searchItemsSet", "false");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  //PDF Modal

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  //Analytics modal
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);

  // For making clicked rows purple
  const [visitedRows, setVisitedRows] = useState(() => {
    const storedVisitedRows =
      JSON.parse(localStorage.getItem("visitedRows")) || [];
    return new Set(storedVisitedRows);
  });

  // To make visited rows persist across sessions

  useEffect(() => {
    const storedVisitedRows =
      JSON.parse(localStorage.getItem("visitedRows")) || [];
    setVisitedRows(new Set(storedVisitedRows));
  }, []);

  useEffect(() => {
    localStorage.setItem("visitedRows", JSON.stringify([...visitedRows]));
  }, [visitedRows]);

  const handleRowClick = (row) => {
    const searchLang = localStorage.getItem("lang") === "ar" ? "arabic" : "english";
    const collection = searchLang === "arabic" ? row.coll_source_arabic : row.coll_source;
    trackGoogleAnalyticsEvent("collection_click", {
      collection: collection || row.coll_source,
      search_language: searchLang,
    });
    setSelectedRow(row);
    setModalOpen(true);
    setVisitedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(row.file_url);
      localStorage.setItem("visitedRows", JSON.stringify([...newSet]));
      return newSet;
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCloseAnalyticsModal = () => {
    setAnalyticsModalOpen(false);
  };

  useEffect(() => {}, [analytics, searchResults]);

  //Loop through searchResults, count the number of each collection type, and store in an array of objects
  const collTypeCounts = [];
  const storedResults = JSON.parse(localStorage.getItem("searchResults"));

  // const updatedResults = searchResults;
  let updatedResults = JSON.parse(localStorage.getItem("analytics"));
  updatedResults = analytics;

  //! Format

  if (
    updatedResults &&
    updatedResults.coll_type &&
    updatedResults.coll_type.buckets
  ) {
    let formatResults = updatedResults.coll_type.buckets;
    formatResults.forEach((result) => {
      const collType = result.key;
      const count = result.doc_count;
      collTypeCounts.push({ coll_type: collType, count: count });
    });
  }

  const lang = localStorage.getItem("lang");
  const isArabic = lang === "ar";

  //! Collection
  const collNameIdentifier = [];
  const collectionKey = isArabic
    ? "analytics_per_collection_arabic"
    : "analytics_per_collection";
  if (
    updatedResults &&
    updatedResults[collectionKey] &&
    updatedResults[collectionKey].buckets
  ) {
    updatedResults[collectionKey].buckets.forEach((result) => {
      const collName = result.key;
      const count = result.doc_count;
      collNameIdentifier.push({
        [isArabic ? "coll_name_arabic" : "coll_name"]: collName,
        count,
      });
    });
  }

  //! Source
  const collSourceCounts = [];
  const sourceKey = isArabic ? "coll_source_arabic" : "coll_source";
  if (
    updatedResults &&
    updatedResults[sourceKey] &&
    updatedResults[sourceKey].buckets
  ) {
    updatedResults[sourceKey].buckets.forEach((result) => {
      const collSource = result.key;
      const count = result.doc_count;
      collSourceCounts.push({
        [isArabic ? "coll_source_arabic" : "coll_source"]: collSource,
        count,
      });
    });
  }

  //! Location
  const collLocationCounts = [];
  const locationKey = isArabic ? "coll_location_arabic" : "coll_location";
  if (
    updatedResults &&
    updatedResults[locationKey] &&
    updatedResults[locationKey].buckets
  ) {
    updatedResults[locationKey].buckets.forEach((result) => {
      const collLocation = result.key;
      const count = result.doc_count;
      collLocationCounts.push({
        [isArabic ? "coll_location_arabic" : "coll_location"]: collLocation,
        count,
      });
    });
  }

  useEffect(() => {
    if (localStorage.getItem("searchItemsSet") === "false") {
      localStorage.setItem("searchResults", JSON.stringify(searchResults));
      localStorage.setItem("searchItemsSet", true);
    }
  }, [searchResults]);

  useEffect(() => {}, [storedResults, searchResults, updatedResults]);

  const handleAnalyticsClick = () => {
    setAnalyticsModalOpen(true);
  };

  return {
    searchResults,
    setSearchResults,
    searchResultSets,
    setSearchResultSets,
    ragSummary,
    setRagSummary,
    handleHeadingClick,
    sortArrowElement,
    handlePageChange,
    currentPage,
    totalPages,
    displayedRows,
    emptyRowsCount,
    handleRowClick,
    modalOpen,
    selectedRow,
    handleCloseModal,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    collTypeCounts,
    collNameIdentifier,
    collSourceCounts,
    collLocationCounts,
    handleShow,
    show,
    handleClose,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    handleRowClick,
    modalOpen,
    selectedRow,
    handleCloseModal,
    handlePageChange,
    currentPage,
    totalPages,
    displayedRows,
    emptyRowsCount,
    handleHeadingClick,
    sortArrowElement,
    searchAfter,
    setSearchAfter,
    loading,
    setLoading,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    collTypeCounts,
    collNameIdentifier,
    collSourceCounts,
    collLocationCounts,
    handleShow,
    show,
    handleClose,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    handleRowClick,
    modalOpen,
    selectedRow,
    handleCloseModal,
    handlePageChange,
    currentPage,
    totalPages,
    displayedRows,
    emptyRowsCount,
    handleHeadingClick,
    sortArrowElement,
    searchAfter,
    setSearchAfter,
    loading,
    setLoading,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    collTypeCounts,
    collNameIdentifier,
    collSourceCounts,
    collLocationCounts,
    fingerprint,
    isNewUser,
    setShow,
    setAnalyticsModalOpen,
    setAnalytics,
    setCurrentPage,
    searchString,
    originalSearchString,
    visitedRows,
    filtersResetToken,
  };
};

export default useSearchResults;